/*
 * <address-autocomplete>
 *
 * Progressive enhancement for the contact form's address block. The Google Maps
 * JS API is injected only on the first focus or pointerdown of the street input,
 * once per page, and only when a key is configured. If the key is missing or the
 * script is blocked, the inputs stay plain text inputs and the form still submits.
 */
(function () {
  if (customElements.get('address-autocomplete')) return;

  var CALLBACK_NAME = '__addressAutocompleteMapsReady';

  // Single-flight loader shared by every instance on the page.
  var mapsLoader = null;

  function loadGoogleMaps(apiKey) {
    if (mapsLoader) return mapsLoader;

    mapsLoader = new Promise(function (resolve, reject) {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve();
        return;
      }

      window[CALLBACK_NAME] = function () {
        try {
          delete window[CALLBACK_NAME];
        } catch (error) {
          window[CALLBACK_NAME] = undefined;
        }
        resolve();
      };

      var script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&libraries=places&loading=async&callback=' +
        CALLBACK_NAME;
      script.async = true;
      script.onerror = function () {
        reject(new Error('Google Maps JS API could not be loaded.'));
      };

      document.head.appendChild(script);
    });

    return mapsLoader;
  }

  var COMPONENT_MAP = {
    street_number: ['streetNumber', 'long_name'],
    route: ['route', 'long_name'],
    locality: ['city', 'long_name'],
    administrative_area_level_1: ['state', 'short_name'],
    postal_code: ['postal', 'long_name'],
  };

  class AddressAutocomplete extends HTMLElement {
    constructor() {
      super();
      this.autocomplete = null;
      this.placeListener = null;
      this.streetInput = null;
      this.onFirstInteraction = this.handleFirstInteraction.bind(this);
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.teardown();
    }

    get apiKey() {
      return (this.dataset.apiKey || '').trim();
    }

    get isEnabled() {
      return this.dataset.enabled === 'true' && this.apiKey !== '';
    }

    init() {
      this.streetInput = this.querySelector('[data-role="street"]');
      if (!this.isEnabled || !this.streetInput) return;

      this.streetInput.addEventListener('focus', this.onFirstInteraction);
      this.streetInput.addEventListener('pointerdown', this.onFirstInteraction);
    }

    reinit() {
      this.teardown();
      this.init();
    }

    removeInteractionListeners() {
      if (!this.streetInput) return;
      this.streetInput.removeEventListener('focus', this.onFirstInteraction);
      this.streetInput.removeEventListener('pointerdown', this.onFirstInteraction);
    }

    teardown() {
      this.removeInteractionListeners();

      if (this.placeListener && typeof this.placeListener.remove === 'function') {
        this.placeListener.remove();
      }
      this.placeListener = null;
      this.autocomplete = null;
    }

    handleFirstInteraction() {
      // One shot per element: drop the listeners before the network request.
      this.removeInteractionListeners();

      var element = this;
      loadGoogleMaps(this.apiKey)
        .then(function () {
          element.initAutocomplete();
        })
        .catch(function () {
          // Blocked, offline, or a bad key: leave the inputs as plain text fields.
        });
    }

    initAutocomplete() {
      if (this.autocomplete || !this.streetInput) return;
      if (!window.google || !window.google.maps || !window.google.maps.places) return;
      if (typeof window.google.maps.places.Autocomplete !== 'function') return;

      var options = { types: ['geocode'] };
      var country = (this.dataset.country || '').trim();
      if (country !== '') {
        options.componentRestrictions = { country: country.toLowerCase() };
      }

      try {
        this.autocomplete = new window.google.maps.places.Autocomplete(this.streetInput, options);
        this.autocomplete.setFields(['address_component']);
      } catch (error) {
        this.autocomplete = null;
        return;
      }

      var element = this;
      this.placeListener = this.autocomplete.addListener('place_changed', function () {
        element.fillFromPlace();
      });
    }

    fillFromPlace() {
      if (!this.autocomplete) return;

      var place = this.autocomplete.getPlace();
      if (!place || !place.address_components) return;

      var parts = { streetNumber: '', route: '', city: '', state: '', postal: '' };

      place.address_components.forEach(function (component) {
        component.types.forEach(function (type) {
          var mapping = COMPONENT_MAP[type];
          if (!mapping) return;
          if (parts[mapping[0]] !== '') return;
          parts[mapping[0]] = component[mapping[1]] || '';
        });
      });

      var street = [parts.streetNumber, parts.route]
        .filter(function (value) {
          return value !== '';
        })
        .join(' ');

      this.setFieldValue('street', street);
      this.setFieldValue('city', parts.city);
      this.setFieldValue('state', parts.state);
      this.setFieldValue('postal', parts.postal);
    }

    setFieldValue(role, value) {
      if (!value) return;

      var input = this.querySelector('[data-role="' + role + '"]');
      if (!input) return;

      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  customElements.define('address-autocomplete', AddressAutocomplete);

  function forEachInstance(root, callback) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('address-autocomplete').forEach(callback);
  }

  document.addEventListener('shopify:section:load', function (event) {
    forEachInstance(event.target, function (element) {
      if (typeof element.reinit === 'function') element.reinit();
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    forEachInstance(event.target, function (element) {
      if (typeof element.teardown === 'function') element.teardown();
    });
  });
})();
